import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { type FC, type ReactNode } from "react";
import { useDialog } from "../../../../../../utils/hooks/use-dialog";
import type { ChannelName } from "../../../../../channels/types";
import { prettyChannelName } from "../../../../../channels/utils";

const constructHelpMessage = (channelName: ChannelName): ReactNode => {
  if (channelName === "work_email" || channelName === "personal_email") {
    return (
      <Stack spacing={1}>
        <Typography variant="body2">
          The last time you tried to send to these people, we were notified that they have blocked{" "}
          {prettyChannelName(channelName)} from your ChangeEngine account. They may have unblocked
          it since then.
        </Typography>
        <Typography variant="body2">
          We&apos;ll try to send this Communication, but it may be blocked again. We will remove any
          remaining blocks within the next 7 days. If you have any questions, please contact{" "}
          <Link href="mailto:support@changeengine.com">support@changeengine.com</Link>
        </Typography>
      </Stack>
    );
  } else if (channelName === "twilio" || channelName === "whatsapp") {
    return (
      <Typography variant="body2">
        These people have opted out of {prettyChannelName(channelName)} by replying “STOP” to one of
        your messages.
      </Typography>
    );
  } else if (channelName === "teams") {
    return (
      <Typography variant="body2">
        The last time you tried to send to these people, we were notified that they have blocked
        your ChangeEngine Teams Bot. They may have unblocked it since then. We&apos;ll try to send
        this Communication, but it may be blocked again.
      </Typography>
    );
  }
};

type Props = {
  channelName: ChannelName;
  clickableText: string;
  suffixText: string;
  children: ReactNode;
};

export const PreviewOptedOutButton: FC<Props> = ({
  clickableText,
  suffixText,
  channelName,
  children,
}) => {
  const dialog = useDialog();

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        data-analytics-id="people-opt-out-channel-button"
      >
        <Link
          component="button"
          type="button"
          color="primary"
          underline="hover"
          variant="caption"
          onClick={() => dialog.handleOpen("opt-out")}
          sx={{ fontWeight: "400" }}
        >
          {clickableText}
        </Link>
        <Typography variant="caption">{suffixText}</Typography>
      </Stack>
      <Dialog open={dialog.open} onClose={dialog.handleClose} fullWidth maxWidth="md">
        <DialogTitle>People opted out of {prettyChannelName(channelName)}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {constructHelpMessage(channelName)}
            {children}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={dialog.handleClose} data-analytics-id="people-opt-out-close-button">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
